import { useState, useCallback } from 'react';
import { apiFetch } from '../../../config/api';
import { debugError } from '../../../utils/debugLog';

/**
 * Custom hook to fetch experiments for a template
 *
 * @returns {Object} Hook interface with loading, error, data, and fetch function
 */
export const useTemplateExperiments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchExperiments = useCallback(async templateId => {
    if (!templateId) {
      setError('Template ID is required');
      return null;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await apiFetch(
        `/api/v1/experiments/?template_id=${templateId}&page_size=100`
      );

      if (response && response.success) {
        setData(response.experiments || []);
        return response.experiments || [];
      } else {
        throw new Error(response?.message || 'Failed to fetch experiments');
      }
    } catch (err) {
      const errorMessage =
        err.message || 'An error occurred while fetching experiments';
      setError(errorMessage);
      debugError('Error fetching template experiments:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return {
    loading,
    error,
    data,
    fetchExperiments,
    clearError,
    clearData,
  };
};

export default useTemplateExperiments;
